import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Strategy, Plus, ArrowClockwise, Archive, Trash, Target, Megaphone, CurrencyDollar, X } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { EmptyState } from "@/promotions/components/EmptyState";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { campaignApi } from "../useCreative";
import { fmtRelative } from "../creativeUtils";

const PLATFORMS = ["instagram", "tiktok", "facebook", "linkedin", "youtube", "x", "pinterest"];

export default function Campaigns() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archived, setArchived] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans(await campaignApi.list(archived)); } catch { setPlans([]); }
    setLoading(false);
  }, [archived]);
  useEffect(() => { load(); }, [load]);

  const archive = async (id) => { await campaignApi.setStatus(id, archived ? "draft" : "archived"); toast.success(archived ? "Restored" : "Archived"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this campaign plan?")) return; await campaignApi.remove(id); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-8" data-testid="campaigns">
      <PromoPageHeader
        overline="Creative Studio" title="AI Campaign Orchestrator"
        description="Describe a campaign objective and let the Orchestrator draft an executable plan — strategy, storyboard, creative, schedule and delivery — coordinating every GrowthAI module. You review and approve; nothing publishes automatically."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setArchived((a) => !a)} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="campaigns-toggle-archived"><Archive size={14} /> {archived ? "Active" : "Archived"}</button>
            <button onClick={load} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="campaigns-refresh"><ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground hover:opacity-90" data-testid="campaigns-new"><Plus size={15} weight="bold" /> New Campaign</button>
          </div>
        }
      />

      {loading ? <CardsSkeleton count={3} /> : plans.length === 0 ? (
        <EmptyState Icon={Strategy} title={archived ? "No archived plans" : "No campaign plans yet"} description={archived ? "Archived plans appear here." : "Create your first AI campaign plan from a high-level objective."} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="campaigns-grid">
          {plans.map((p) => (
            <div key={p.id} className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-ai/40" data-testid={`campaign-${p.id}`}>
              <button onClick={() => nav(`/creative/campaigns/${p.id}`)} className="w-full text-left" data-testid={`campaign-open-${p.id}`}>
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ai/15 text-ai"><Strategy size={18} weight="fill" /></div>
                  <StatusBadge status={p.status} />
                </div>
                <h3 className="truncate text-base font-semibold">{p.title}</h3>
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.input?.objective}</p>
                <div className="mt-4 space-y-1.5 text-xs">
                  <Row Icon={Target} label="Audience" value={p.input?.audience || "—"} />
                  <Row Icon={Megaphone} label="Platforms" value={(p.input?.platforms || []).join(", ") || "—"} />
                  <Row Icon={CurrencyDollar} label="Est. cost" value={p.estimated_cost != null ? `$${p.estimated_cost}` : "—"} />
                  <Row Icon={ArrowClockwise} label="Modified" value={fmtRelative(p.updated_at)} />
                </div>
              </button>
              <div className="mt-4 flex items-center gap-1 border-t border-border pt-3 opacity-0 transition-opacity group-hover:opacity-100">
                <button onClick={() => archive(p.id)} title={archived ? "Restore" : "Archive"} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`campaign-archive-${p.id}`}><Archive size={15} /></button>
                <button onClick={() => del(p.id)} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive" data-testid={`campaign-del-${p.id}`}><Trash size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <NewCampaignForm onClose={() => setShowForm(false)} onCreated={(p) => nav(`/creative/campaigns/${p.id}`)} />}
    </div>
  );
}

function Row({ Icon, label, value }) {
  return <div className="flex items-center gap-1.5"><Icon size={13} className="text-muted-foreground" /><span className="text-muted-foreground">{label}:</span><span className="truncate font-medium">{value}</span></div>;
}

function NewCampaignForm({ onClose, onCreated }) {
  const [f, setF] = useState({ objective: "", product: "", audience: "", language: "en", budget: "", schedule: "", duration: "", constraints: "", platforms: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const togglePlatform = (p) => setF((s) => ({ ...s, platforms: s.platforms.includes(p) ? s.platforms.filter((x) => x !== p) : [...s.platforms, p] }));

  const submit = async () => {
    if (!f.objective.trim()) return toast.error("Enter a campaign objective");
    setBusy(true);
    try {
      const body = { ...f, budget: f.budget ? Number(f.budget) : null };
      const p = await campaignApi.create(body);
      toast.success("Campaign plan drafted");
      onCreated(p);
    } catch { toast.error("Could not generate plan"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="campaign-form">
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="text-base font-semibold">New Campaign Plan</h3><button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button></div>
        <Fld label="Objective *"><textarea rows={2} value={f.objective} onChange={(e) => set("objective", e.target.value)} placeholder="Launch our new eco water bottle to Gen-Z with a viral reel series" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-objective" /></Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Product / Service"><input value={f.product} onChange={(e) => set("product", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-product" /></Fld>
          <Fld label="Audience"><input value={f.audience} onChange={(e) => set("audience", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-audience" /></Fld>
        </div>
        <Fld label="Platforms">
          <div className="flex flex-wrap gap-1.5">
            {PLATFORMS.map((p) => (
              <button key={p} type="button" onClick={() => togglePlatform(p)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${f.platforms.includes(p) ? "border-ai bg-ai/10 text-ai" : "border-border text-muted-foreground hover:bg-accent"}`} data-testid={`campaign-platform-${p}`}>{p}</button>
            ))}
          </div>
        </Fld>
        <div className="grid grid-cols-3 gap-3">
          <Fld label="Budget"><input type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-budget" /></Fld>
          <Fld label="Schedule"><input value={f.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="next week" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-schedule" /></Fld>
          <Fld label="Duration"><input value={f.duration} onChange={(e) => set("duration", e.target.value)} placeholder="2 weeks" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="campaign-duration" /></Fld>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">Cancel</button>
          <button onClick={submit} disabled={busy} className="flex items-center gap-1.5 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground hover:opacity-90 disabled:opacity-60" data-testid="campaign-generate">
            {busy ? <ArrowClockwise size={15} className="animate-spin" /> : <Strategy size={15} weight="fill" />} {busy ? "Orchestrating…" : "Generate Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div><label className="promo-overline mb-1 block">{label}</label>{children}</div>;
}
